// Workspace Primer copy for the AI Acquisition OS install card.
//
// Voice: customer-facing per docs/content/shared/customer-facing-voice-rule.md.
// You. Plain English. No "Builder," no "operating repo," no "starter," no
// "pack." The reader has just paid $1,997; they're about to install something
// they don't yet have a mental model for. Three screens to set the scene, then
// one setup message for Claude Code or Codex, then Module 1.
//
// Each screen is short by design. A coach scrolls past walls of text.

export interface PrimerScreen {
  /** Short label shown in the progress dots / stepper. */
  stepLabel: string;
  /** Eyebrow above the title -- quick orientation. */
  eyebrow: string;
  /** Headline; 4-8 words. */
  title: string;
  /** Body copy. Multi-paragraph. Plain language. */
  body: string[];
  /** Optional video placeholder shown before the body. */
  video?: {
    label: string;
    title: string;
    description: string;
    duration: string;
  };
  /** Optional action cards shown after the body. */
  cards?: Array<{
    label: string;
    title: string;
    body: string;
  }>;
  /** Optional callout pulled out to the side (a single thing to remember). */
  callout?: {
    label: string;
    text: string;
  };
  /** Label for the primary button on this screen. */
  primaryLabel: string;
  /** Label for the secondary "back" button on this screen, if any. */
  secondaryLabel?: string;
}

export const workspacePrimerScreens: PrimerScreen[] = [
  {
    stepLabel: 'Welcome',
    eyebrow: 'Welcome video',
    title: 'Welcome to AI Acquisition OS.',
    video: {
      label: 'Video placeholder',
      title: 'Welcome from Tom',
      description:
        'A short welcome video will thank you for joining, show what you now own, and explain why the workspace comes before the Build Room lessons.',
      duration: '~3 min',
    },
    body: [
      "Thank you for purchasing AI Acquisition OS. This is where we get your workspace ready before you start the first Build Room module.",
      "You are not being asked to become technical. You are opening an AI app, choosing the folder you want to use, then starting Module 1 where we explain what the setup means and how to use it properly.",
      "The whole flow is simple: choose your AI app, open the folder for this workspace, add the workspace files, then start Module 1.",
    ],
    primaryLabel: 'Next: choose your AI app',
  },
  {
    stepLabel: 'AI app',
    eyebrow: 'Before the workspace',
    title: 'Open Claude Code or Codex.',
    body: [
      "You only need one AI build app to begin. Use Codex Desktop if that is where you want to work. Use Claude Code if you already prefer it. You can switch later.",
      "If you already have one installed, open it now. If not, install one first, then come back to this page. The next step gives you a setup message to paste into that app.",
    ],
    cards: [
      {
        label: 'Option 01',
        title: 'Codex Desktop',
        body: 'Best if you want a desktop app experience and plain-language guidance while the workspace is created.',
      },
      {
        label: 'Option 02',
        title: 'Claude Code',
        body: 'Best if you already use Claude Code and want the workspace created from there.',
      },
    ],
    primaryLabel: 'My AI app is ready',
    secondaryLabel: 'Back',
  },
  {
    stepLabel: 'Workspace',
    eyebrow: 'One folder on your Mac',
    title: 'Use your own business folder.',
    body: [
      "Create or open the folder you want to use for this workspace. Ideally it is named after your business or project, but an empty folder called something like New project is fine. You can rename it later.",
      "AI Acquisition OS installs into that folder. It holds the business context, skills, proof, and working files for the system you just bought.",
      "On the next screen, copy one setup message and paste it into Claude Code or Codex while that folder is open. The AI app will add the workspace files and tell you when it is ready.",
      "Do not worry about **/welcome** yet. The slash skills appear after you start a fresh chat in the installed folder, and Module 1 explains what the welcome conversation is, why it matters, and when to run it.",
    ],
    callout: {
      label: 'What happens next',
      text: 'After the workspace is ready, come back here and start Module 1: The Shift.',
    },
    primaryLabel: 'Create my setup message',
    secondaryLabel: 'Back',
  },
];

/**
 * Static setup instructions shown after the Builder copies the setup message.
 * Keeps them oriented while they switch to Claude Code or Codex and back.
 */
export const postInstallInstructions = {
  title: 'While your AI app sets this up',
  steps: [
    {
      label: "Step 1",
      body: "Open Claude Code or Codex Desktop in the folder you want to use. A generic empty folder name is fine; you can rename it later.",
    },
    {
      label: "Step 2",
      body: "Paste the setup message you copied. If it asks permission to add the workspace files, approve it.",
    },
    {
      label: "Step 3",
      body: "When it says the workspace is ready, start one fresh chat in the same folder so the new slash skills appear. Then come back here and click Workspace ready.",
    },
  ],
  troubleshooting: {
    title: "Stuck?",
    text: "The private setup link is refreshed every time you copy the message. If Claude or Codex says it expired, come back here and press Copy setup message again. If /welcome is missing after install, open a fresh AI app chat in the same business folder.",
  },
};
